import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import envConfig from '../../config';
import {
  previewUsers,
  inviteUsers,
  fetchUsers,
  updateUserStatus,
  resendUserInvitation,
  updateUser,
  disableUserMFA,
} from '../routines';
import {
  VALID,
  INVALID_EMAIL,
  USER_EXISTS,
  DEFAULT_ROLE_ID,
} from '../../common/constants/UserManagementConstants';

import { isValidEmailAddress } from '../../common/util/validatorUtil';
import { request } from '../../common/util/RestClient';
import SUCCESS_MESSAGES from '../../common/constants/SuccessMessagesConstants';
import USER_MANAGEMENT_EVENTS from '../../common/constants/UserManagementEvents';
import { invalidateUsersList } from '../routines/userManagementRoutines';
import { selectLoggedInUser } from '../selectors/userSelector';

function getSuccessMessage(event) {
  switch (event) {
    case USER_MANAGEMENT_EVENTS.REVOKE_INVITATION:
      return SUCCESS_MESSAGES.REVOKE_INVITATION;

    case USER_MANAGEMENT_EVENTS.DEACTIVATE:
      return SUCCESS_MESSAGES.DEACTIVATE_USER;

    case USER_MANAGEMENT_EVENTS.REACTIVATE:
      return SUCCESS_MESSAGES.REACTIVATE_USER;

    case USER_MANAGEMENT_EVENTS.CHANGE_ROLE:
      return SUCCESS_MESSAGES.CHANGE_USER_ROLE;

    case USER_MANAGEMENT_EVENTS.INVITE:
      return SUCCESS_MESSAGES.INVITE_USER;

    case USER_MANAGEMENT_EVENTS.DISABLE_MFA:
      return SUCCESS_MESSAGES.DISABLE_USER_MFA;

    default:
      return SUCCESS_MESSAGES.EDIT_INFO;
  }
}

export function* previewUsersSaga({ payload }) {
  try {
    const { users } = payload;
    const validatedUsers = {};
    const userNamesToValidate = [];
    users.forEach((user) => {
      const isValidUserName = isValidEmailAddress(user);
      let status = VALID;

      if (isValidUserName) {
        userNamesToValidate.push(user);
      } else {
        status = INVALID_EMAIL;
      }

      validatedUsers[user] = {
        key: user,
        email: user,
        roleId: DEFAULT_ROLE_ID,
        isSelected: true,
        status,
      };
    });
    if (userNamesToValidate.length !== 0) {
      const URL = `${envConfig.APIG_URL}user-management/users/validate-by-usernames`;
      const config = {
        method: 'POST',
        payload: { usernames: userNamesToValidate },
      };
      const response = yield call(request, URL, config);
      Object.keys(response.data).forEach((username) => {
        if (response.data[username] === 'EXIST') {
          validatedUsers[username] = {
            ...validatedUsers[username],
            status: USER_EXISTS,
          };
        }
      });
    }

    yield put(previewUsers.success({ previewedUsers: validatedUsers }));
  } catch (error) {
    yield put(previewUsers.failure({ error }));
  }
}

export function* inviteUsersSaga({ payload }) {
  try {
    const { users } = payload;
    const data = users.map((user) => ({
      email: user.email,
      roleId: user.roleId,
      displayCurrency: user.displayCurrency,
    }));

    const URL = `${envConfig.APIG_URL}users`;
    const config = {
      method: 'POST',
      payload: { data },
    };
    const response = yield call(request, URL, config);
    yield put(fetchUsers.trigger());
    yield put(
      inviteUsers.success({
        response,
        successMessage: SUCCESS_MESSAGES.INVITE_USER,
      })
    );
  } catch (error) {
    yield put(inviteUsers.failure({ error }));
  }
}

export function* fetchUsersSaga() {
  try {
    const URL = `${envConfig.APIG_URL}users`;
    const response = yield call(request, URL);
    yield put(fetchUsers.success(response.data));
  } catch (error) {
    yield put(fetchUsers.failure({ error }));
  }
}
export async function validateExternalIdExist({ externalId }) {
  let URL = `${envConfig.APIG_URL}users`;
  if (externalId) {
    URL = `${URL}?externalId=${encodeURIComponent(externalId)}`;
  } else {
    throw new Error('No Value Entered!');
  }

  try {
    const response = await request(URL);
    return response;
  } catch (error) {
    return null;
  }
}
export function* updateUserStatusSaga({ payload }) {
  try {
    const { userId, stateControlData, status, event, userType, email } =
      payload;
    const URL = `${envConfig.APIG_URL}/users/${userId}/status`;
    const config = {
      method: 'PATCH',
      payload: { status, userId, userType, email },
    };
    yield call(request, URL, config);
    const successMessage = getSuccessMessage(event);

    yield put(
      updateUserStatus.success({
        stateControlData,
        successMessage,
      })
    );
    yield put(fetchUsers.trigger());
  } catch (error) {
    yield put(updateUserStatus.failure({ error }));
  }
}

export function* resendUserInvitationSaga({ payload }) {
  try {
    const { userId, stateControlData } = payload;
    const URL = `${envConfig.APIG_URL}user-management/users/${userId}/resend-invitation`;
    const config = {
      method: 'PATCH',
      payload: {},
    };
    yield call(request, URL, config);
    yield put(
      resendUserInvitation.success({
        stateControlData,
        successMessage: SUCCESS_MESSAGES.RESEND_INVITATION,
      })
    );
  } catch (error) {
    yield put(resendUserInvitation.failure({ error }));
  }
}

export function* updateUserSaga({ payload }) {
  try {
    const { userId, stateControlData, event, ...data } = payload;

    const URL = `${envConfig.APIG_URL}user-management/users/${userId}`;
    const config = {
      method: 'PATCH',
      payload: data,
    };
    yield call(request, URL, config);
    const successMessage = getSuccessMessage(event);
    yield put(
      updateUser.success({
        stateControlData,
        successMessage,
      })
    );
    yield put(fetchUsers.trigger());
  } catch (error) {
    yield put(updateUser.failure({ error }));
  }
}

export function* disableUserMFASaga({ payload }) {
  try {
    const { userId, stateControlData, event } = payload;
    const URL = `${envConfig.APIG_URL}user-management/users/${userId}/disable-mfa`;
    const config = {
      method: 'PATCH',
      payload: {},
    };
    yield call(request, URL, config);
    const successMessage = getSuccessMessage(event);
    yield put(
      disableUserMFA.success({
        stateControlData,
        successMessage,
      })
    );
    yield put(fetchUsers.trigger());
  } catch (error) {
    yield put(disableUserMFA.failure({ error }));
  }
}

// TODO this shall be removed once we move 2FA verify token call to BE. We need this call because there's no other way to invalidate the Users List cache in the BE,
// since 2FA setup flow is happening in FE (calls made directly to Cognito) at the time of writing this comment. So we are using a hack / workaround by making a fake user update to trigger invalidation of the Users List cache.
function* invalidateUsersListSaga() {
  try {
    const { userId, mobileNumber } = yield select(selectLoggedInUser);
    yield put(updateUser({ userId, mobileNumber }));
  } catch (error) {
    yield put(invalidateUsersList.failure({ error }));
  }
}

export default function* root() {
  yield all([takeLatest(invalidateUsersList.TRIGGER, invalidateUsersListSaga)]);
  yield all([takeLatest(updateUser.TRIGGER, updateUserSaga)]);
  yield all([takeLatest(previewUsers.TRIGGER, previewUsersSaga)]);
  yield all([takeLatest(inviteUsers.TRIGGER, inviteUsersSaga)]);
  yield all([takeLatest(fetchUsers.TRIGGER, fetchUsersSaga)]);
  yield all([takeLatest(updateUserStatus.TRIGGER, updateUserStatusSaga)]);
  yield all([
    takeLatest(resendUserInvitation.TRIGGER, resendUserInvitationSaga),
  ]);
  yield all([takeLatest(disableUserMFA.TRIGGER, disableUserMFASaga)]);
}
